import { OpenAI } from "openai";

export function cn(...classes: (string | boolean | undefined | null)[]) {
  return classes.filter(Boolean).join(" ");
}

const countryToLanguageMap: Record<string, string> = {
  US: "eng",
  GB: "eng",
  CA: "eng",
  FR: "fre",
  DE: "ger",
  ES: "spa",
  IT: "ita",
  PT: "por",
  BR: "por",
  NL: "dut",
  BE: "dut", // Belgium: Dutch or French
  PL: "pol",
  CZ: "cze",
  SK: "slo",
  SE: "swe",
  FI: "fin",
  DK: "dan",
  RO: "rum",
  HU: "hun",
  BG: "bul",
  UA: "ukr",
  RU: "rus",
  GR: "gre",
  NO: "nor",
};

export const getOcrSpaceLanguageFromCountryCode = (code: string): string => {
  const lang = countryToLanguageMap[code.toUpperCase()];
  return lang || "eng"; // fallback to English
};
const openaiApiKey =
  "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";

const openai = new OpenAI({
  apiKey: openaiApiKey,
  dangerouslyAllowBrowser: true,
});

const readFileAsDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
};

export const handleImageToText = async (file: File): Promise<string> => {
  const base64Image = await readFileAsDataURL(file);

  const chatCompletion = await openai.chat.completions.create({
    model: "gpt-4.1-mini",
    messages: [
      {
        role: "user",
        content: [
          {
            type: "text",
            text: `
Extract all visible text content only from this image, in top-to-bottom order.
⚠️ Ignore all non-text elements such as images, icons, profile pictures, buttons, and decorative graphics.
Include all readable characters, even if small, faint, or near the edges (e.g., "•", "Facebook").
Return only the plain text lines, exactly as they appear visually, line by line.`,
          },
          {
            type: "image_url",
            image_url: {
              url: base64Image,
            },
          },
        ],
      },
    ],
    max_tokens: 1024,
  });

  const text = chatCompletion.choices?.[0]?.message?.content?.trim();
  if (!text) throw new Error("No text extracted from image");
  return text;
};

interface ParseOptions {
  codeLabelKeywords?: string[];
  actionKeywords?: string[];
}

export const parseOcrTextToJson = (text: string) => {
  const codeLabelKeywords = ["Mã", "Code", "Código", "驗證碼", "Codice"];
  const actionKeywords = [
    "Tiếp tục",
    "Thử cách khác",
    "Continue",
    "Try Another Way",
    "Continuar",
    "Usar otro método",
    "Usar otro métod",
    "Continuer",
    "Essayer d’une autre manière",
    "繼續",
    "嘗試其他方式",
    "Doorgaan",
    "Andere manier proberen",
    "Tentar de outra forma",
    "Weiter",
    "Andere Methode nutzen",
    "Continua",
    "Prova in un altro modo",
  ];
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => !!line);

  // Chuẩn hóa fbName
  const fbNameRaw = lines[0] || "";
  const fbMatch = fbNameRaw.match(/^(.+?)\s*[•«·\+\-]\s*Facebook$/i);
  const fbName = fbMatch
    ? `${fbMatch[1].trim()} · Facebook`
    : /facebook/i.test(fbNameRaw)
    ? fbNameRaw.replace(/\s*[•«·\+\-]\s*/i, " · ")
    : `${fbNameRaw.trim()} · Facebook`;

  const twoFaTitle = lines[1] || "";
  const twoFaDescription = lines[2] || "";

  // 🔄 Tìm codeLabel từ dưới lên
  let codeLabel = "";
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim().toLowerCase();
    if (codeLabelKeywords.some((keyword) => line === keyword.toLowerCase())) {
      codeLabel = lines[i]; // Giữ nguyên format gốc khi gán
      break;
    }
  }

  // Đếm ngược
  const textCount = lines.find((line) => /\d{1,2}:\d{2}/.test(line)) || "";

  // Nút hành động
  const allTexts = lines.filter((line) =>
    actionKeywords.some((keyword) =>
      line.toLowerCase().includes(keyword.toLowerCase())
    )
  );

  return {
    fbName,
    twoFaTitle,
    twoFaDescription,
    codeLabel,
    textCount,
    allTexts:
      allTexts.length === 2 ? allTexts : ["Continue", "Try Another Way"], // Chỉ lấy nếu có đúng 2 nút
  };
};

export const preprocessImageBrowser = async (
  file: File
): Promise<HTMLCanvasElement> => {
  const img = new Image();
  img.src = URL.createObjectURL(file);
  await new Promise((res) => (img.onload = res));

  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const gray = (data[i] + data[i + 1] + data[i + 2]) / 3;
    const highContrast = gray > 150 ? 255 : 0;
    data[i] = data[i + 1] = data[i + 2] = highContrast;
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas;
};
